import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SiteHeader } from '../components/SiteHeader';
import { SEO } from '../components/SEO';

/**
 * Map a `?service=<slug>` query param to a friendly headline + a
 * pre-filled message prompt so customers landing from the Services page
 * already have context written in. We never auto-submit – they confirm
 * and edit before sending.
 */
const SERVICE_PROMPTS: Record<string, { headline: string; intent: string; prompt: string }> = {
  adhoc: {
    headline: 'Ad-hoc filing enquiry',
    intent: 'I would like a quote for an ad-hoc Companies House filing.',
    prompt: `Hi,\n\nI'd like a quote for an ad-hoc filing.\n\nCompany number: \nCompany name: \nWhat I need to file (e.g. change of registered office, director appointment, director resignation, director details change, etc.): \nDetails: \n\nThanks`,
  },
  authcode: {
    headline: 'Request a Companies House authentication code',
    intent: "I'd like you to request my company's authentication code on my behalf.",
    prompt: `Hi,\n\nI'd like you to request my Companies House authentication code on my behalf.\n\nCompany number: \nCompany name: \nDirector name: \nRegistered office address (where the code will be posted): \n\nThanks`,
  },
  'non-dormant-help': {
    headline: "Help filing accounts — company didn't file dormant accounts last year",
    intent:
      "Our website flagged that this company had trading activity in its last filed accounts, so AA02 dormant accounts may not be the correct form. I'd like help choosing and filing the correct set of accounts.",
    // {companyName} and {companyNumber} are substituted from query params
    // in the component below so the customer sees their own details
    // already filled in.
    prompt: `Hi,\n\nYour website flagged that my company had trading activity in the previous accounting period, so AA02 dormant accounts may not be the correct form for this year.\n\nPlease advise on the correct filing.\n\nMy details:\n- Company name: {companyName}\n- Company number: {companyNumber}\n\nThanks`,
  },
};

interface FormState {
  name: string;
  email: string;
  phone: string;
  companyName: string;
  message: string;
  website: string; // honeypot – must stay empty
}

const EMPTY: FormState = {
  name: '',
  email: '',
  phone: '',
  companyName: '',
  message: '',
  website: '',
};

export function Contact() {
  const [params] = useSearchParams();
  const service = params.get('service');
  const prompt = service ? SERVICE_PROMPTS[service] : null;
  // Optional company context passed by the Confirm-page modal so the
  // customer's own company appears pre-filled in the message body.
  // We use safe substitution (no HTML/JSX) so a malicious URL can't
  // inject markup - message is a plain <textarea>.
  const companyName = params.get('companyName') || '';
  const companyNumber = params.get('companyNumber') || '';
  const initialMessage = prompt
    ? prompt.prompt
        .replace('{companyName}', companyName)
        .replace('{companyNumber}', companyNumber)
    : '';

  const [form, setForm] = useState<FormState>(
    prompt
      ? { ...EMPTY, message: initialMessage, companyName }
      : EMPTY,
  );
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus('sending');
    setError(null);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Failed (${res.status})`);
      setStatus('sent');
      setForm(EMPTY);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Could not send. Try again.');
    }
  }

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="Contact — Dormant Accounts Filing"
        description="Get in touch with Dormant Accounts Filing. Questions about dormant accounts, confirmation statements, or company closure — we usually reply within a working day."
        path="/contact"
      />
      <SiteHeader />

      <main className="mx-auto grid max-w-5xl gap-10 px-6 py-12 sm:py-16 md:grid-cols-5">
        {/* Left column – pitch + alternative contact info */}
        <aside className="md:col-span-2">
          <Link to={prompt ? '/services' : '/'} className="text-sm text-brand-700 hover:underline">
            ← Back to {prompt ? 'services' : 'home'}
          </Link>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
            {prompt ? prompt.headline : 'Get in touch'}
          </h1>
          <p className="mt-3 text-slate-600">
            {prompt
              ? `${prompt.intent} Share the details below and we'll come back with a fixed-fee quote the same working day.`
              : `Question about a filing, the auth code, refunds, or anything else? Send us a note and we'll get back to you the same working day.`}
          </p>

          <div className="mt-8 space-y-5 text-sm">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 text-brand-600">✉️</span>
              <div>
                <div className="font-semibold text-slate-900">Email us directly</div>
                <a href="mailto:contact.dormantfiling@gmail.com" className="text-brand-700 hover:underline">
                  contact.dormantfiling@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="mt-0.5 text-brand-600">⏱</span>
              <div>
                <div className="font-semibold text-slate-900">Response time</div>
                <div className="text-slate-600">Usually within a few hours, Monday–Friday.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="mt-0.5 text-brand-600">🇬🇧</span>
              <div>
                <div className="font-semibold text-slate-900">UK-based team</div>
                <div className="text-slate-600">Real people, not chatbots.</div>
              </div>
            </div>
          </div>
        </aside>

        {/* Right column – the form */}
        <div className="md:col-span-3">
          {status === 'sent' ? (
            <div className="rounded-xl bg-green-50 p-8 ring-1 ring-green-200">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-2xl text-green-700">
                ✓
              </div>
              <h2 className="mt-4 text-xl font-semibold text-green-900">Message sent</h2>
              <p className="mt-2 text-sm text-green-800">
                Thanks for reaching out. We'll be in touch shortly – usually within a few hours.
              </p>
              <button
                onClick={() => setStatus('idle')}
                className="btn-ghost mt-6"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="rounded-xl bg-white p-6 ring-1 ring-slate-200 sm:p-8">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Name" required>
                  <input
                    type="text"
                    value={form.name}
                    onChange={set('name')}
                    required
                    autoComplete="name"
                    className="input"
                    placeholder="Jane Smith"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    required
                    autoComplete="email"
                    className="input"
                    placeholder="jane@example.com"
                  />
                </Field>
                <Field label="Phone (optional)">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={set('phone')}
                    autoComplete="tel"
                    className="input"
                    placeholder="07123 456 789"
                  />
                </Field>
                <Field label="Company (optional)">
                  <input
                    type="text"
                    value={form.companyName}
                    onChange={set('companyName')}
                    autoComplete="organization"
                    className="input"
                    placeholder="Acme Ltd"
                  />
                </Field>
              </div>

              <Field label="Message" required className="mt-4">
                <textarea
                  value={form.message}
                  onChange={set('message')}
                  required
                  minLength={10}
                  rows={6}
                  className="input"
                  placeholder="How can we help?"
                />
              </Field>

              {/* Honeypot – hidden via off-screen + aria. Bots fill in every
                  visible field they find; visible: false to humans. */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={set('website')}
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
              />

              {error && (
                <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="btn-primary mt-6 w-full justify-center"
              >
                {status === 'sending' ? 'Sending…' : 'Send message'}
              </button>
              <p className="mt-3 text-center text-[11px] text-slate-500">
                By submitting, you agree to our{' '}
                <Link to="/privacy" className="hover:underline">Privacy Policy</Link>.
              </p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

function Field({
  label,
  required,
  children,
  className = '',
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="text-amber-600"> *</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
